import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/interview_modal.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { setupBrandNameProperties } from 'AppBranding';

const templateUrl = cacheAngularTemplate(angularModule, template);

// The server resolves the calendar from the program type. It passes the calendar down through the
// `calendly-base-url` attribute (see ProgramApplication#interview_calendly_url). The `/interview` redirect
// uses the same calendar, and the `cohort:invite_to_interview` event carries it for the Customer.io emails.
//
// CALENDLY_URL_DEFAULT is a defensive fallback for the unexpected case when the server sends no calendar.
const CALENDLY_URL_DEFAULT = 'https://calendly.com/quantic-interviews/15min';
const CALENDLY_SCRIPT_URL = 'https://assets.calendly.com/assets/external/widget.js';
const TRANSLATION_PREFIX = 'careers.post_apply_interview_modal';

function interviewModalFactory($injector) {
    const DialogModal = $injector.get('DialogModal');
    const $rootScope = $injector.get('$rootScope');
    const $document = $injector.get('$document');
    const $window = $injector.get('$window');
    const $sce = $injector.get('$sce');
    const safeDigest = $injector.get('safeDigest');
    const TranslationHelper = $injector.get('TranslationHelper');
    const ErrorLogService = $injector.get('ErrorLogService');

    return {
        restrict: 'E',
        scope: {
            nextStepKey: '@',
            selectivityMessageKey: '@',
            calendlyBaseUrl: '@',
            programApplicationId: '@',
        },
        templateUrl,

        link(scope) {
            setupBrandNameProperties($injector, scope);

            Object.defineProperty(scope, 'currentUser', {
                get() {
                    return $rootScope.currentUser;
                },
            });

            // Initialize modal state
            scope.currentPage = 1;

            const userEmail = scope.currentUser?.email || '';
            const userName = scope.currentUser?.name || '';
            if (!scope.calendlyBaseUrl) {
                ErrorLogService.notifyInProd('Post-apply interview modal has no Calendly URL', null, {
                    level: 'warning',
                    program_application_id: scope.programApplicationId,
                });
            }
            const calendlyBaseUrl = scope.calendlyBaseUrl || CALENDLY_URL_DEFAULT;
            const calendlyParams = new URLSearchParams({
                hide_event_type_details: '1',
                hide_gdpr_banner: '1',
                email: userEmail,
                name: userName,
            });
            if (scope.programApplicationId) calendlyParams.set('utm_term', scope.programApplicationId);
            scope.calendlyUrl = `${calendlyBaseUrl}?${calendlyParams}`;

            // Resolve translations
            const translationHelper = new TranslationHelper(TRANSLATION_PREFIX);
            const fallbackHtml = translationHelper.get('calendly_fallback', {
                calendlyUrl: scope.calendlyUrl,
            });
            scope.calendlyFallbackHtml = $sce.trustAsHtml(fallbackHtml);

            const selectivityMessageHtml = translationHelper.get(scope.selectivityMessageKey);
            scope.selectivityMessageHtml = $sce.trustAsHtml(selectivityMessageHtml);

            scope.titleText = translationHelper.get('title');
            scope.nextStepText = translationHelper.get(scope.nextStepKey);
            scope.thankYouText = translationHelper.get('thank_you_message');
            scope.closeButtonText = translationHelper.get('close_button');

            // Load Calendly script
            loadCalendlyScript();

            // Listen for Calendly events
            function handleCalendlyEvent(e) {
                if (e.data && e.data.event === 'calendly.event_scheduled') {
                    goToPageTwo();
                }
            }

            $window.addEventListener('message', handleCalendlyEvent);

            // Clean up event listener when directive is destroyed
            scope.$on('$destroy', () => {
                $window.removeEventListener('message', handleCalendlyEvent);
            });

            function loadCalendlyScript() {
                // Check if script is already loaded
                if ($document[0].querySelector(`script[src="${CALENDLY_SCRIPT_URL}"]`)) {
                    return;
                }

                const script = $document[0].createElement('script');
                script.src = CALENDLY_SCRIPT_URL;
                script.async = true;
                $document[0].body.appendChild(script);
            }

            function goToPageTwo() {
                scope.currentPage = 2;
                safeDigest(scope);
            }

            scope.closeModal = () => {
                DialogModal.hideAlerts();
            };
        },
    };
}

angularModule.directive('interviewModal', ['$injector', interviewModalFactory]);
angularModule.directive('instantInterviewModal', ['$injector', interviewModalFactory]);
angularModule.directive('optionalInterviewModal', ['$injector', interviewModalFactory]);
